import React, { useState } from 'react';
import { Box, InputAdornment, styled, TextField } from '@mui/material';
import { ReactComponent as CloseIcon } from '../assets/icons/24/close.svg';
import { ReactComponent as EyeOpenIcon } from '../assets/icons/24/eye_open_gray.svg';
import { ReactComponent as EyeClosedIcon } from '../assets/icons/24/eye_closed_gray.svg';
import { getThemeColors } from '../helpers/themeStyles';

const ERROR_COLOR = '#FF6669';

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ theme, $hasValue, $error }) => {
  const colors = getThemeColors(theme);

  return {
    '& .MuiOutlinedInput-root': {
      color: colors.inputText,
      height: 44,
      backgroundColor: 'transparent',
      borderRadius: 24,
      paddingLeft: 20,
      paddingRight: 20,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: $error ? ERROR_COLOR : colors.inputBorder,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: $error ? ERROR_COLOR : colors.inputBorder,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
        borderColor: $error ? ERROR_COLOR : colors.inputFocus,
      },
      '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
        borderColor: $error ? ERROR_COLOR : colors.inputFocus,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: $hasValue ? '18px 0px 6px' : '10px 0px',
      fontSize: 16,
      borderRadius: 0,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
      padding: '18px 0px 6px',
    },
    '& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:hover, & .MuiOutlinedInput-input:-webkit-autofill:focus':
      {
        WebkitTextFillColor: colors.inputText,
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
        boxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
        borderRadius: 0,
        transition: 'background-color 9999s ease-out 0s',
      },
    '& .MuiInputLabel-root': {
      color: colors.inputLabel,
      fontSize: 16,
      transform: 'translate(20px, 10px) scale(1)',
      transformOrigin: 'top left',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      fontSize: 10,
      transform: 'translate(20px, 5px) scale(1)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: $error ? ERROR_COLOR : colors.inputFocus,
    },
    ...($error && {
      '& .MuiInputLabel-root': {
        color: ERROR_COLOR,
      },
      '& .MuiFormHelperText-root': {
        color: ERROR_COLOR,
      },
    }),
  };
});

const CloseIconButton = styled(InputAdornment)({
  cursor: 'pointer',
});

const EndAdornmentContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const InputField = (props) => {
  const {
    InputLabelProps,
    InputProps,
    onClear,
    withPasswordToggle = false,
    error = false,
    ...restProps
  } = props;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const hasValue = Boolean(restProps.value?.toString().trim());
  const { type, ...forwardedProps } = restProps;
  const isPasswordField = type === 'password';
  const showPasswordToggle = withPasswordToggle && isPasswordField;
  const clearAdornment = onClear && (
    <CloseIconButton position="end" onClick={onClear}>
      <CloseIcon />
    </CloseIconButton>
  );
  const passwordToggleAdornment = showPasswordToggle && (
    <CloseIconButton position="end" onClick={() => setIsPasswordVisible((prev) => !prev)}>
      {isPasswordVisible ? <EyeOpenIcon /> : <EyeClosedIcon />}
    </CloseIconButton>
  );
  const combinedAdornment =
    clearAdornment || passwordToggleAdornment ? (
      <EndAdornmentContainer>
        {passwordToggleAdornment}
        {clearAdornment}
      </EndAdornmentContainer>
    ) : (
      InputProps?.endAdornment
    );

  return (
    <StyledTextField
      variant="outlined"
      size="small"
      $hasEndAdornment={Boolean(combinedAdornment)}
      $error={error}
      type={showPasswordToggle && isPasswordVisible ? 'text' : type}
      $hasValue={hasValue}
      error={error}
      InputLabelProps={{ ...InputLabelProps }}
      InputProps={{
        ...InputProps,
        notched: false,
        endAdornment: combinedAdornment,
      }}
      {...forwardedProps}
    />
  );
};

export default InputField;
